/**
 * The issue slip, printed on A4 as two identical halves: one for the store,
 * one for the kitchen. Each is signed by both and the sheet is cut across
 * the dotted line. The store has an ordinary printer, not a receipt roll,
 * which is why it is a page and not a strip.
 */
import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { formatServerTime } from '@foodrun/shared';
import { packs } from './ItemPicker.jsx';

export function IssueSlipSheet({ issueId, onClose }) {
  const { data, isLoading, error } = useQuery({ queryKey: ['store', 'issue', issueId], queryFn: () => api.get(`/store/issues/${issueId}`) });
  return (
    <div role="dialog" aria-modal="true" aria-label="Print issue slip" className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl bg-surface p-4" onClick={(e) => e.stopPropagation()}>
        <div className="max-h-[70vh] overflow-y-auto rounded-lg border border-rule bg-white p-4 text-black">
          {isLoading && <Spinner />}
          {error && (
            <p role="alert" className="text-sm text-bad">
              {error.message}
            </p>
          )}
          {data && <IssueSlip data={data} />}
        </div>
        <div className="no-print mt-3 flex gap-2">
          <Button variant="secondary" size="lg" className="flex-1" onClick={onClose}>
            Close
          </Button>
          <Button size="lg" className="flex-1" disabled={!data} onClick={() => window.print()}>
            Print
          </Button>
        </div>
      </div>
    </div>
  );
}

export function IssueSlip({ data }) {
  const { issue, lines } = data;
  const toKitchen = issue.direction === 'to_kitchen';
  const when = formatServerTime(issue.created_at);
  const Copy = ({ label }) => (
    <div style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif', color: '#000', padding: '4mm 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '2px solid #000', paddingBottom: 4 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Food Run · {toKitchen ? 'Issue to kitchen' : 'Return to store'}</div>
          <div style={{ fontSize: 12 }}>
            {issue.transfer_number}
            {issue.request_number ? ` · against request ${issue.request_number}` : ''} · {when}
          </div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>{label} copy</div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 6 }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid #000' }}>
            <th style={{ padding: '3px 0' }}>#</th>
            <th style={{ padding: '3px 0' }}>Item</th>
            <th style={{ padding: '3px 0', textAlign: 'right' }}>Packs</th>
            <th style={{ padding: '3px 0', textAlign: 'right' }}>Quantity</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={l.ingredient_id} style={{ borderBottom: '1px solid #bbb' }}>
              <td style={{ padding: '3px 0' }}>{i + 1}</td>
              <td style={{ padding: '3px 0' }}>{l.name}</td>
              <td style={{ padding: '3px 0', textAlign: 'right' }}>
                {packs(l.packs)} × {l.pack_name || l.unit}
              </td>
              <td style={{ padding: '3px 0', textAlign: 'right' }}>
                {packs(Number(l.quantity))} {l.unit}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {issue.notes && <div style={{ fontSize: 12, marginTop: 6 }}>Note: {issue.notes}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, marginTop: 22, fontSize: 12 }}>
        <div style={{ flex: 1, borderTop: '1px solid #000', paddingTop: 3 }}>Store: {issue.issued_by || ''}</div>
        <div style={{ flex: 1, borderTop: '1px solid #000', paddingTop: 3 }}>Kitchen: {issue.requested_by_name || ''}</div>
      </div>
    </div>
  );
  return (
    <div className="statement">
      <Copy label="Store" />
      <div style={{ borderTop: '2px dashed #000', margin: '6mm 0', textAlign: 'center', fontSize: 10, color: '#000' }}>✂ cut here</div>
      <Copy label="Kitchen" />
    </div>
  );
}
